import React from "react";
import FilterBar, { computeRange, useSharedFilter } from "../FilterBar";
import { buildParams } from "../api";
import { useAdminData } from "../useAdminData";
import { Panel, KpiCard, MetricStrip, Skeleton, EmptyState, ErrorState, PageHeader, Badge, fmtNum, fmtPct } from "../Primitives";
import { LineChart } from "../Charts";

const defaultFilter = () => ({ preset: "30d", ...computeRange("30d") });

export default function Visitors() {
  const [filter, setFilter] = useSharedFilter(defaultFilter);
  const { data, loading, error, reload } = useAdminData(async (api) => {
    const p = buildParams(filter);
    return (await api.get("/admin/visitors", { params: { date_from: p.date_from, date_to: p.date_to } })).data;
  }, [filter]);

  return (
    <div data-testid="visitors-page">
      <PageHeader title="Visitantes" meta="Identificador anônimo por navegador, sem dados pessoais e sem fingerprinting." />
      <FilterBar value={filter} onChange={setFilter} show={{ platform: false, provider: false, device: false }} />
      {error && <ErrorState message={error} onRetry={reload} />}

      {loading && !data ? <Skeleton className="h-[86px] mb-6" /> : data && (
        <MetricStrip cols={5} className="mb-6">
          <KpiCard label="Ativos no período" value={data.active_visitors} testid="kpi-active-visitors" />
          <KpiCard label="Novos" value={data.new_visitors} hint="Primeira visita no período" testid="kpi-new-visitors" />
          <KpiCard label="Recorrentes" value={data.returning_visitors} hint="Voltaram em outro dia" testid="kpi-returning-visitors" />
          <KpiCard label="Com eventos nos últimos 5 min" value={data.recent_5min.visitors} hint={`${fmtNum(data.recent_5min.sessions)} sessões · não é contagem de online`} testid="kpi-recent-visitors" />
          <KpiCard label="Eventos sem ID de visitante" value={data.events_without_visitor} hint="Anteriores a esta versão ou com Do Not Track" testid="kpi-no-visitor" />
        </MetricStrip>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.3fr] gap-4">
        <Panel title="Retenção" description={data ? `Exibida apenas com pelo menos ${data.min_cohort} visitantes elegíveis.` : undefined} testid="retention-panel">
          {data && (
            <table className="ops-table" data-testid="retention-table">
              <thead><tr><th>Dia</th><th className="!text-right">Elegíveis</th><th className="!text-right">Voltaram</th><th className="!text-right">Taxa</th></tr></thead>
              <tbody>
                {data.retention.map((r) => (
                  <tr key={r.day} data-testid={`retention-d${r.day}`}>
                    <td className="mono">D{r.day}</td>
                    <td className="num">{fmtNum(r.eligible)}</td>
                    <td className="num">{fmtNum(r.returned)}</td>
                    <td className="num">{r.sufficient ? fmtPct(r.rate) : <Badge tone="muted">dados insuficientes</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="px-4 py-3 ops-help border-t border-[var(--border)]">
            D<i>n</i> = visitantes cuja primeira visita foi há pelo menos <i>n</i> dias e que voltaram exatamente no dia <i>n</i> após a primeira visita (calendário de Brasília).
          </p>
        </Panel>
        <Panel title="Visitantes ativos por dia">
          {data?.daily_active?.length ? <LineChart data={data.daily_active} /> : <EmptyState title="Sem visitantes identificados no período" />}
        </Panel>
      </div>
    </div>
  );
}
